import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';
import { IOrgChart } from './organization.state';

export interface ITemplate {
  id: number;
  name: string;
  description: string;
  preview_image_url: string;
  created_at: string;
  updated_at: string;
}

export interface ITemplateDetail {
  template: ITemplate;
  nodes: {
    id: number;
    template_id: number;
    name: string;
    parent_id: number | null;
    lft: number;
    rgt: number;
  }[];
}

interface OrgChartTemplateState {
  templates: ITemplate[];
  selectedTemplate: ITemplateDetail | null;
  isLoading: boolean;
  isApplying: boolean;
  error: unknown;

  fetchedTemplatesDetails: Record<number, ITemplateDetail>;

  fetchTemplates: () => Promise<void>;
  fetchTemplateDetails: (id: number) => Promise<void>;
  fetchAllTemplateDetails: (templateIds: number[]) => Promise<void>;
  applyTemplate: (id: number, tenantId: number) => Promise<void>;
  clearSelectedTemplate: () => void;
}

export const useOrgChartTemplateState = create<OrgChartTemplateState>((set, get) => ({
  templates: [],
  selectedTemplate: null,
  fetchedTemplatesDetails: {},
  isLoading: false,
  isApplying: false,
  error: null,

  fetchTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/templates/org-structures`);
      set({ templates: data.data, isLoading: false });
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
    }
  },

  fetchTemplateDetails: async (id: number) => {
    // Check cache first
    const cached = get().fetchedTemplatesDetails[id];
    if (cached) {
      set({ selectedTemplate: cached });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const { data } = await axiosInstance.get(
        `${env.apiTasklistBaseUrl}/api/v1/compro/templates/org-structures/${id}`,
      );
      set(state => ({
        selectedTemplate: data.data,
        isLoading: false,
        fetchedTemplatesDetails: {
          ...state.fetchedTemplatesDetails,
          [id]: data.data,
        },
      }));
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
    }
  },

  fetchAllTemplateDetails: async (templateIds: number[]) => {
    // Avoid re-fetching if already cached
    const state = get();
    const uncachedIds = templateIds.filter(id => !state.fetchedTemplatesDetails[id]);

    if (uncachedIds.length === 0) return;

    // Use Promise.all to fetch concurrently
    // Note: If there are many templates, we might want to batch this,
    // but for < 20 templates, parallel requests are okay-ish, or better:
    // update backend to send details in list (ideal) or bulk fetch (not available).
    // We'll proceed with concurrent fetches for now.

    // Use a separate loading state or just keep it silent?
    // Ideally silent update for grid previews.

    try {
      const results = await Promise.allSettled(
        uncachedIds.map(id =>
          axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/templates/org-structures/${id}`),
        ),
      );

      const newDetails: Record<number, ITemplateDetail> = {};

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const detail = result.value.data.data;
          if (detail && detail.template && detail.template.id) {
            newDetails[detail.template.id] = detail;
          }
        }
      });

      if (Object.keys(newDetails).length > 0) {
        set(state => ({
          fetchedTemplatesDetails: {
            ...state.fetchedTemplatesDetails,
            ...newDetails,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to fetch some template details', err);
    }
  },

  applyTemplate: async (id: number, tenantId: number) => {
    set({ isApplying: true, error: null });
    try {
      await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/templates/org-structures/${id}/apply`, {
        tenant_id: tenantId,
      });
      toast.success('Template applied successfully');
      set({ isApplying: false });
    } catch (err) {
      errorHandler(err);
      set({ isApplying: false, error: err });
      throw err;
    }
  },

  clearSelectedTemplate: () => set({ selectedTemplate: null }),
}));
